import React, { useState } from 'react';
import Button from '../../atoms/Button';
import Icon from '../../atoms/Icon';
import { parseExcelFile, generateExcelTemplate } from '../../../utils/excelUtils';
import { Product, ProductCollection } from '../../../types';
import './BatchUploadModal.css';
import FileSaver from 'file-saver';

interface BatchUploadModalProps {
  onClose: () => void;
  onUpload: (products: Partial<Product>[]) => void;
  collections: ProductCollection[];
  loading?: boolean;
}

const BatchUploadModal: React.FC<BatchUploadModalProps> = ({
  onClose,
  onUpload,
  collections,
  loading = false
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'parsing' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [parsedProducts, setParsedProducts] = useState<Partial<Product>[]>([]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const selectedFile = e.target.files[0];
      
      // Check if the file is an Excel file
      if (!selectedFile.name.endsWith('.xlsx')) {
        setErrorMessage('Please select a valid Excel (.xlsx) file');
        setUploadStatus('error');
        return;
      }
      
      setFile(selectedFile);
      setUploadStatus('idle');
      setErrorMessage('');
    }
  };

  const handleParseFile = async () => {
    if (!file) return;
    
    setUploadStatus('parsing');
    setErrorMessage('');
    
    try {
      const products = await parseExcelFile(file);
      
      // Validate products - only check that we have at least one product
      if (products.length === 0) {
        setErrorMessage('No valid products found in the Excel file. Please check the file format.');
        setUploadStatus('error');
        return;
      }
      
      // Check that each product has at least a name or description
      const invalidProducts = products.filter(p => !p.name && !p.description);
      if (invalidProducts.length > 0) {
        setErrorMessage(`${invalidProducts.length} products are missing both name and description. At least one is required.`);
        setUploadStatus('error');
        return;
      }
      
      // Set collection IDs based on collection names
      const productsWithCollectionIds = products.map(product => {
        const collection = collections.find(c => c.name === product.collection);
        const productWithCollectionId = {
          ...product,
          collectionId: collection?.id || ''
        };
        
        // Debug logging for each product
        console.log('🔍 BatchUploadModal - Product with collection ID:', productWithCollectionId);
        
        // Fix flavor prices if they're undefined (but preserve 0 values)
        if (productWithCollectionId.flavors && Array.isArray(productWithCollectionId.flavors)) {
          productWithCollectionId.flavors = productWithCollectionId.flavors.map(flavor => ({
            ...flavor,
            price: flavor.price === undefined || flavor.price === null ? 0 : flavor.price
          }));
        }
        
        // Check for undefined values
        Object.entries(productWithCollectionId).forEach(([key, value]) => {
          if (value === undefined) {
            console.error(`❌ UNDEFINED VALUE FOUND in BatchUploadModal: ${key} = ${value}`);
          }
        });
        
        return productWithCollectionId;
      });
      
      console.log('🔍 BatchUploadModal - All products with collection IDs:', productsWithCollectionIds);
      setParsedProducts(productsWithCollectionIds);
      setUploadStatus('success');
    } catch (error) {
      console.error('Error parsing Excel file:', error);
      setErrorMessage('Failed to parse Excel file. Please check the file format.');
      setUploadStatus('error');
    }
  };

  const handleUpload = () => {
    onUpload(parsedProducts);
  };

  const handleDownloadTemplate = () => {
    const excelBlob = generateExcelTemplate();
    FileSaver.saveAs(excelBlob, 'product_upload_template.xlsx');
  };

  return (
    <div className="batch-upload-modal-backdrop">
      <div className="batch-upload-modal">
        <div className="batch-upload-modal-header">
          <h2>Batch Upload Products</h2>
          <button className="close-button" onClick={onClose}>×</button>
        </div>
        
        <div className="batch-upload-modal-content">
          <p>Upload multiple products at once using an Excel file.</p>
          
          <div className="template-download">
            <Button 
              variant="secondary"
              onClick={handleDownloadTemplate}
              className="template-download-button"
            >
              <Icon type="download" size="small" />
              Download Template
            </Button>
            <span className="template-info">Use this template to format your product data correctly.</span>
          </div>
          
          <div className="file-upload-container">
            <input
              type="file"
              id="excel-upload"
              accept=".xlsx"
              onChange={handleFileChange}
              className="file-input"
            />
            <label htmlFor="excel-upload" className="file-upload-label">
              <Icon type="upload" size="medium" />
              <span>{file ? file.name : 'Choose Excel File'}</span>
            </label>
          </div>
          
          {file && uploadStatus === 'idle' && (
            <Button 
              variant="primary"
              onClick={handleParseFile}
              className="parse-button"
            >
              Parse File
            </Button>
          )}
          
          {uploadStatus === 'parsing' && (
            <div className="loading-indicator">
              <span>Parsing file...</span>
            </div>
          )}
          
          {uploadStatus === 'error' && (
            <div className="error-message">
              <Icon type="error" size="small" />
              <span>{errorMessage}</span>
            </div>
          )}
          
          {uploadStatus === 'success' && (
            <div className="success-message">
              <p>Successfully parsed {parsedProducts.length} products.</p>
              <div className="product-summary">
                <h4>Products to Upload:</h4>
                <ul className="product-list">
                  {parsedProducts.slice(0, 5).map((product, index) => (
                    <li key={index}>
                      <strong>{product.name}</strong> - {product.collection}
                    </li>
                  ))}
                  {parsedProducts.length > 5 && (
                    <li>...and {parsedProducts.length - 5} more</li>
                  )}
                </ul>
              </div>
            </div>
          )}
        </div>
        
        <div className="batch-upload-modal-footer">
          <Button 
            variant="secondary" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          {uploadStatus === 'success' && (
            <Button 
              variant="primary" 
              onClick={handleUpload}
              disabled={loading}
              className="upload-button"
            >
              {loading ? 'Uploading...' : `Upload ${parsedProducts.length} Products`}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchUploadModal;
